import * as React from 'react';
import type Hls from 'hls.js';
import {resolveVideoSource, resolveAudioSource, ResolvedMediaSource} from './media-source.use-case';
import {PlayerSurface, PlayerStatus, AudioCover} from './MediaPlayer.style';

export interface MediaPlayerProps {
  src?: string;
  mime?: string;
  alternativeSrc?: string;
  poster?: string;
  kind?: 'video' | 'audio';
  title?: string;
  onError?: () => void;
  onDuration?: (duration: number) => void;
}

type PlaybackProps = {source: ResolvedMediaSource; poster?: string; title: string; onFailure: (message: string) => void; onDuration?: (duration: number) => void};
const failureMessage = 'Não foi possível reproduzir esta mídia. Verifique o link ou tente novamente.';

function VideoPlayer({source, poster, title, onFailure, onDuration}: PlaybackProps) {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const callbacks = React.useRef({onFailure, onDuration});
  callbacks.current = {onFailure, onDuration};
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let cancelled = false;
    let instance: Hls | undefined;
    let recovered = false;
    setLoading(true);
    const fail = (message: string) => {
      if (cancelled) return;
      setLoading(false);
      callbacks.current.onFailure(message);
    };
    if (source.kind === 'hls' && !video.canPlayType('application/vnd.apple.mpegurl')) {
      import('hls.js').then(({default: HlsPlayer}) => {
        if (cancelled) return;
        if (!HlsPlayer.isSupported()) {
          fail('Este navegador não suporta este formato de vídeo.');
          return;
        }
        instance = new HlsPlayer();
        instance.on(HlsPlayer.Events.MANIFEST_PARSED, () => {if (!cancelled) setLoading(false);});
        instance.on(HlsPlayer.Events.ERROR, (_event, data) => {
          if (cancelled || !data.fatal) return;
          if (!recovered && data.type === HlsPlayer.ErrorTypes.MEDIA_ERROR) {
            recovered = true;
            instance?.recoverMediaError();
            return;
          }
          fail(data.response?.code === 403 ? 'Este link expirou ou não permite acesso. Atualize o link do vídeo.' : failureMessage);
        });
        instance.loadSource(source.url);
        instance.attachMedia(video);
      }).catch(() => fail(failureMessage));
    } else {
      video.src = source.url;
      video.load();
    }
    return () => {
      cancelled = true;
      instance?.destroy();
      video.removeAttribute('src');
      video.load();
    };
  }, [source.url, source.kind, source.mime]);
  return <>
    <video ref={videoRef} controls playsInline preload="metadata" poster={poster} aria-label={title}
      onLoadedMetadata={event => {setLoading(false); const duration = event.currentTarget.duration; if (Number.isFinite(duration)) callbacks.current.onDuration?.(duration);}}
      onCanPlay={() => setLoading(false)} onError={() => onFailure(failureMessage)}>
      {source.kind === 'native' && <source src={source.url} type={source.mime} />}
    </video>
    {loading && <PlayerStatus role="status">Carregando vídeo…</PlayerStatus>}
  </>;
}

function AudioPlayer({source, poster, title, onFailure, onDuration}: PlaybackProps) {
  return <>
    {poster && <AudioCover src={poster} alt="" onError={event => {event.currentTarget.hidden = true;}} />}
    <audio key={source.url} controls preload="metadata" aria-label={title} onError={() => onFailure(failureMessage)}
      onLoadedMetadata={event => {if (Number.isFinite(event.currentTarget.duration)) onDuration?.(event.currentTarget.duration);}}>
      <source src={source.url} type={source.mime} />
    </audio>
  </>;
}

function VimeoEmbed({source, title, onFailure}: PlaybackProps) {
  return <iframe src={source.url} title={title} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen onError={() => onFailure(failureMessage)} />;
}

function PlaybackSession({src, mime, alternativeSrc, poster, kind = 'video', title = 'Prévia da mídia', onError, onDuration}: MediaPlayerProps) {
  const [fallback, setFallback] = React.useState(false);
  const [error, setError] = React.useState('');
  const [attempt, setAttempt] = React.useState(0);
  const source = (kind === 'audio' ? resolveAudioSource : resolveVideoSource)({url: fallback ? alternativeSrc : src, mime: fallback ? undefined : mime});
  const onFailure = (message: string) => {
    if (!fallback && alternativeSrc && alternativeSrc !== src) setFallback(true);
    else {setError(message); onError?.();}
  };
  const props = {source, poster, title, onFailure, onDuration};
  const invalid = source.kind === 'none';
  const externalUrl = /^https?:\/\//i.test(src || '') ? src : undefined;
  return <PlayerSurface onClick={event => event.stopPropagation()} onKeyDown={event => event.stopPropagation()}>
    {error || invalid ? <PlayerStatus role="alert">
      <p>{error || 'Esta fonte não pode ser reproduzida aqui.'}</p>
      {externalUrl && <a href={externalUrl} target="_blank" rel="noopener noreferrer">{source.kind === 'vimeo-embed' || /vimeo\.com/.test(externalUrl) ? 'Abrir no Vimeo' : 'Abrir mídia em nova aba'}</a>}
      <button type="button" aria-label="Tentar reproduzir novamente" onClick={() => {setFallback(false); setError(''); setAttempt(attempt + 1);}}>Tentar novamente</button>
    </PlayerStatus> : kind === 'audio' ? <AudioPlayer {...props} /> : source.kind === 'vimeo-embed' ? <VimeoEmbed {...props} /> : <VideoPlayer key={`${source.url}:${attempt}`} {...props} />}
  </PlayerSurface>;
}

export function MediaPlayer(props: MediaPlayerProps) {
  return <PlaybackSession key={`${props.kind}:${props.src}:${props.mime}:${props.alternativeSrc}`} {...props} />;
}
